import { Entity } from '../model';
import { logoutAction } from '../reducer/authentication';
import { store } from '../store';
import { retry } from '../utils';
import PromiseQueue from '../utils/PromiseQueue';
import AuthService from './AuthService';
import { LicenseError, PermissionError, UnauthorizedError, ValidationError } from './Errors';
import Settings from './Settings';

const DEFAULT_PERPAGE = 50

export interface SafeLiteApiErrorResponse {
    message: string;
}

export function isErrorResponse(error: any): error is SafeLiteApiErrorResponse {

    if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
            if (typeof error['message'] === 'string') {
                return true;
            }
        }
    }

    return false;
}

export interface FetchOptions {
    method?: string,
    body?: BodyInit,
    headers?: { [key: string]: string }
}

export async function refreshTokenAndRetry(url: string, options?: FetchOptions) {
    console.log('refreshTokenAndRetry')
    if (!options) {
        options = {};
    }
    const tokens = await AuthService.instance.refreshToken();
    if (tokens) {
        options.headers = getRequestHeader(tokens.access_token);
        return await fetch(url, options);
    }
    return null;
}

export function getRequestHeader(access_token: string | null, useAuthentication: boolean = true): Record<string, string> {

    var header = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    };

    if (useAuthentication) {
        header = Object.assign(header, { 'Authorization': `Bearer ${access_token}` });
    }

    return header;
}

export function getBaseUrl(model: string) {
    const { apiProtocol, apiHost, apiPort, apiPath } = Settings.get();
    return `${apiProtocol}://${apiHost}:${apiPort}${apiPath}/${model}/`;
}

export function getBaseApiUrl(): string {
    const { apiProtocol, apiHost, apiPort, apiPath } = Settings.get();
    return `${apiProtocol}://${apiHost}:${apiPort}${apiPath}/`;
}

export function getUploadUrl(): string {
    const { apiProtocol, apiHost, apiPort, apiPath, apiFileSystemDriver } = Settings.get();
    if (apiFileSystemDriver === 'aws') {
        return `https://safedeskuploads.s3.sa-east-1.amazonaws.com/`;
    }
    return `${apiProtocol}://${apiHost}:${apiPort}${apiPath}/uploads/`;
}


export type ConditionOperator = '=' | '!=' | '<>' | '>' | '>=' | '<' | '<=' | '><' | 'in' | 'notIn' | 'ilike' | 'null' | '!null';

export type ArrayCondition<T> = [keyof T, ConditionOperator, ...any[]] | [string, ConditionOperator, ...any[]];

export type WhereCondition<T> = { [P in keyof T]?: any } | ArrayCondition<T>[] | undefined;

export interface WhereLeaf<T> {
    field: keyof T | string;
    op: ConditionOperator;
    value: any;
}

export interface WhereTree<T> {
    operator: 'and' | 'or';
    conditions: Array<WhereLeaf<T> | WhereTree<T>>;
}

export interface ConditionsOptions<T> {
    where?: WhereCondition<T>;
    orWhere?: WhereCondition<T>;
    whereTree?: WhereTree<T>
}

type Prop<T> = keyof T;

export interface GetOptions<T> {
    fields?: Prop<T>[];
    relations?: string[];
    withTrashed?: boolean;
}

export interface FindOneOptions<T> extends ConditionsOptions<T>, GetOptions<T> { }

export type OrderBy = 'ASC' | 'DESC';

export type OrderByOption<T> = { [P in keyof T]?: OrderBy } | Record<string, OrderBy>;

export interface FindManyOptions<T> extends FindOneOptions<T> {
    order?: OrderByOption<T>;
    limit?: number;
}

export interface PageOptions {
    page?: number,
    per_page?: number
}

export interface PaginationOptions<T> extends FindManyOptions<T>, PageOptions {

}

const queue: PromiseQueue<any, any> = new PromiseQueue<any, any>();

export interface PaginationResult<T> {
    data: T[];
    current_page: number;
    per_page: number;
    last_page: number;
    from: number;
    to: number;
    total: number;
}

type METHOD = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'OPTIONS';

export async function promiseRequest(url: string, method?: METHOD, body?: Object, throwError?: boolean, useAuthentication: boolean = true) {

    return await queue.enqueueAsync(null, async () => {
        try {

            var response = await retry(async () => {
                var access_token = AuthService.instance.getAccessToken();
                if (!access_token && useAuthentication) {
                    throw new Error(`Não autorizado ${url}`);
                }
                var options: FetchOptions = {
                    method: method || 'GET',
                    headers: getRequestHeader(access_token, useAuthentication)
                }
                if (body) {
                    options.body = typeof body === 'string' ? body : JSON.stringify(body)
                }

                var response = await fetch(url, options);

                if (response.status === 401) {

                    const refreshResponse = await refreshTokenAndRetry(url, options);
                    if (refreshResponse) {
                        response = refreshResponse;
                    } else {
                        throw new Error('Unauthorized');
                    }
                }

                if (response.ok === false) {
                    const text = await response.text();
                    if (response.status === 403) {
                        const r = JSON.parse(text);
                        throw new PermissionError(r && r.message);
                    } else if (response.status === 422) {
                        const r = JSON.parse(text);
                        throw new ValidationError(r && r.message);
                    }
                    let message = response.statusText;
                    try {
                        const r = JSON.parse(text);
                        if (r && r.message) message = r.message;
                    } catch { /* not JSON */ }
                    throw new Error(message);
                }

                return response;
            }, 5, 1000);

            if (response.status === 204) {
                return true;
            }

            const text = await response.text();

            if (response.ok) {
                try {
                    return (typeof text === 'string' ? JSON.parse(text) : null);
                } catch (e) {
                    return text;
                }
            } else {
                if (response.status === 401) {
                    await AuthService.instance.logout();
                    store.dispatch(logoutAction());
                    throw new UnauthorizedError(text);
                }
                if (response.status === 402) {
                    const r = JSON.parse(text);
                    throw new LicenseError(r && r.message);
                }
                if (response.status === 403) {
                    const r = JSON.parse(text);
                    throw new PermissionError(r && r.message);
                }
                if (response.status === 422) {
                    const r = JSON.parse(text);
                    throw new ValidationError(r && r.message);
                }
                throw new Error(text);
            }
        } catch (e) {
            console.log('Request Error', e);
            if (throwError) {
                throw e;
            } else {
                return null;
            }
        }
    });
}

export async function promiseGetRequest(url: string) {
    return promiseRequest(url, 'GET');
}

export class ServiceApi<T extends Entity> {

    public useLimit: boolean;

    constructor(protected model: string) {
        this.getUrl = this.getUrl.bind(this);
        this.getBaseUrl = this.getBaseUrl.bind(this);
        this.getList = this.getList.bind(this);
        this.getById = this.getById.bind(this);
        this.insert = this.insert.bind(this);
        this.update = this.update.bind(this);
        this.delete = this.delete.bind(this);
        this.promiseGetBlob = this.promiseGetBlob.bind(this);
        //this.insertWithId = this.insertWithId.bind(this);
        this.useLimit = false;
    }

    getBaseUrl() {
        var model = this.model;
        return getBaseUrl(model);
    }

    count(options: ConditionsOptions<T>): Promise<number> {
        var { where, orWhere, whereTree } = options;
        var baseUrl = this.getBaseUrl();
        var params = [];
        const whereParams = this.getWhereParams(where);
        params.push(...whereParams);
        const orWhereParams = this.getOrWhereParams(orWhere);
        params.push(...orWhereParams);
        if (whereTree) {
            params.push(`whereTree=${encodeURIComponent(JSON.stringify(whereTree))}`);
        }
        var url = baseUrl + `count/?` + params.join('&');
        return promiseGetRequest(url);
    }

    getWhereParams(where?: WhereCondition<T>) {
        var params = [];
        if (Array.isArray(where)) {
            where.forEach((w, i) => {
                if (Array.isArray(w)) {
                    params.push(`where[]=${w.map(c => encodeURIComponent(c)).join(',')}`);
                }
            });
        } else if (typeof where === 'object' && where !== null) {
            for (var prop in where) {
                var value = where[prop];
                params.push(`where[]=${prop},=,${encodeURIComponent(value)}`);
            }
        }
        return params;
    }

    getOrWhereParams(orWhere?: WhereCondition<T>) {
        var params = [];
        if (Array.isArray(orWhere)) {
            orWhere.forEach((w, i) => {
                if (Array.isArray(w)) {
                    params.push(`orWhere[]=${w.map(c => encodeURIComponent(c)).join(',')}`);
                }
            });
        } else if (typeof orWhere === 'object' && orWhere !== null) {
            for (var prop in orWhere) {
                var value = orWhere[prop];
                params.push(`orWhere[]=${prop},=,${encodeURIComponent(value)}`);
            }
        }
        return params;
    }

    getOrderParams(order?: OrderByOption<any>) {
        var params: string[] = [];
        if (order) {
            for (var orderKey in order) {
                var ord = order[orderKey];
                params.push(`order[${orderKey}]=${ord}`);
            }
        }
        return params;
    }

    getRelationsParams(relations?: string[]) {
        var params: string[] = [];
        if (Array.isArray(relations)) {
            for (var key in relations) {
                var relation = relations[key];
                params.push(`relations[]=${relation}`);
            }
        }
        return params;
    }

    getSelectParams(fields?: Prop<T>[]) {
        var params: string[] = [];
        if (Array.isArray(fields)) {
            for (var key in fields) {
                var selection = fields[key];
                params.push(`fields[]=${String(selection)}`);
            }
        }
        return params;
    }

    getPaginationParams({ page, per_page }: PageOptions): string[] {
        var params: string[] = [];
        if (page !== undefined && per_page !== undefined) {
            params.push(`page=${page}`);
            params.push(`per_page=${per_page}`);
        }
        return params;
    }

    getOptionsParams(options: PaginationOptions<T>): string[] {
        var params = [];

        var { order, where, fields, relations, orWhere, limit, whereTree, withTrashed } = options || {};

        var params = [];

        const whereParams = this.getWhereParams(where);
        const orWhereParams = this.getOrWhereParams(orWhere);
        const orderParams = this.getOrderParams(order);
        const relationParams = this.getRelationsParams(relations);
        const selectParams = this.getSelectParams(fields);
        const paginationParams = this.getPaginationParams(options);

        params.push(...whereParams);
        params.push(...orWhereParams);
        if (whereTree) {
            params.push(`whereTree=${encodeURIComponent(JSON.stringify(whereTree))}`);
        }
        if (withTrashed) {
            params.push(`withTrashed=${withTrashed}`);
        }
        params.push(...orderParams);
        params.push(...relationParams);
        params.push(...selectParams);
        params.push(...paginationParams);
        if (limit) {
            params.push(`limit=${limit}`);
        }

        return params;
    }

    getUrl(url: string, options: PaginationOptions<T>): string {
        const params = this.getOptionsParams(options);
        return url + '?' + params.join('&');
    }

    getList(options: FindManyOptions<T>): Promise<T[]>;
    getList(): Promise<T[]>;

    getList(options: FindManyOptions<T> = {}): Promise<T[]> {
        var baseUrl = this.getBaseUrl();
        var url = baseUrl + `list/`;
        url = this.getUrl(url, options);
        return promiseGetRequest(url);
    }

    getPagination(options: PaginationOptions<T>): Promise<PaginationResult<T>>;
    getPagination(): Promise<PaginationResult<T>>;

    getPagination(options: PaginationOptions<T> = {}): Promise<PaginationResult<T>> {
        var baseUrl = this.getBaseUrl();
        var url = baseUrl + `paginate/`;
        url = this.getUrl(url, options);
        return promiseGetRequest(url);
    }

    getById(id: number | string, options: GetOptions<T> = {}): Promise<T> {
        var baseUrl = this.getBaseUrl();
        var url = this.getUrl(baseUrl + `${id}/`, options);
        return promiseGetRequest(url);
    }

    async update(id: number | string, item: Partial<T>, throwError: boolean = true): Promise<boolean> {
        var baseUrl = this.getBaseUrl();
        var url = baseUrl + `${id}/`;
        var result = await promiseRequest(url, 'PUT', item, throwError);
        return result && result !== null;
    }

    insert(item: Partial<T>, throwError?: boolean): Promise<T> {
        var baseUrl = this.getBaseUrl();
        return promiseRequest(baseUrl, 'POST', item, throwError);
    }

    async delete(id: number | string): Promise<boolean> {
        var baseUrl = this.getBaseUrl();
        var url = baseUrl + `${id}/`;

        var result = await promiseRequest(url, 'DELETE');
        return result && result !== null;
    }

    promiseGetBlob(url: string, useAuthentication: boolean = true): Promise<Blob | null> {
        var accessToken = AuthService.instance.getAccessToken();
        var options = useAuthentication ? {
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        } : {};
        return fetch(url, options).then(response => {
            if (response && response.ok) {
                return response.blob();
            } else {
                return null;
            }
        });
    }
}

export async function promiseGetBlob(url: string) {
    var accessToken = AuthService.instance.getAccessToken();
    var options = {
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    };
    return fetch(url, options).then(response => {
        if (response && response.ok) {
            return response.blob();
        } else {
            return null;
        }
    });
}

export async function promiseUploadFile(url: string, method: string, fieldName: string, file: File) {
    var accessToken = AuthService.instance.getAccessToken();
    var formData = new FormData();
    formData.append(fieldName, file);
    var options = {
        method,
        headers: {
            "Accept": "application/json",
            'Authorization': `Bearer ${accessToken}`
        },
        body: formData
    };

    const response = await fetch(url, options);

    if (response) {
        const text = await response.text();
        if (response.ok) {
            return (text ? JSON.parse(text) : true);
        } else {
            throw new Error(text);
        }
    }
}

export async function promiseSendFormData(url: string, method: string, body: FormData) {
    var accessToken = AuthService.instance.getAccessToken();
    var options = {
        method,
        headers: {
            "Accept": "application/json",
            'Authorization': `Bearer ${accessToken}`
        },
        body
    };

    const response = await fetch(url, options);

    if (response) {
        const text = await response.text();
        if (response.ok) {
            return (text ? JSON.parse(text) : true);
        } else {
            throw new Error(text);
        }
    }
}

export async function downloadBlob(dir: string, fileName: string) {
    var accessToken = AuthService.instance.getAccessToken();
    var url = getBaseApiUrl() + `download/?dir=${dir}`;
    var options = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    };

    const response = await fetch(url, options);

    if (response) {
        const blobData = await response.blob();
        var url = window.URL.createObjectURL(blobData);
        var a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove();  //afterwards we remove the element again       
    }
}

